import { SlidersHorizontal } from "lucide-react";
import { TH, TD } from "../../../../components/ui";
import { TOOLS } from "../../../../mock/config.mock";
import SectionShell, { SubSection } from "../SectionShell";
import useSectionDraft from "../useSectionDraft";
import { CellNumber, CellPct } from "../cells";

const RATE_FIELDS = [
  { key: "laborRatePainting", label: "Labor rate – painting", suffix: "$/h" },
  { key: "laborRateFinishing", label: "Labor rate – finishing (Level 5 skim)", suffix: "$/h" },
];

const PCT_FIELDS = [
  { key: "overheadPct", label: "Company overhead" },
  { key: "profitPct", label: "Profit / markup" },
  { key: "sundriesPct", label: "Sundries (of paint $)" },
  { key: "bondPct", label: "Bonds & insurance" },
  { key: "subMarkupPct", label: "Subcontractor markup" },
  { key: "salesTaxPct", label: "Sales tax on materials" },
];

const SCHEDULE_FIELDS = [
  { key: "hoursPerDay", label: "Hours per crew day", suffix: "h" },
  { key: "workDaysPerMonth", label: "Work days per month", suffix: "d" },
];

const CONTAINERS = [
  { key: "quart", label: "Quart" },
  { key: "gallon", label: "Gallon" },
  { key: "fiveGal", label: "5-gal pail" },
];

function LabeledCell({ label, children }) {
  return (
    <label className="flex items-center justify-between gap-3 rounded-[4px] border border-border_soft bg-surface_alt px-3 py-2">
      <span className="text-[12.5px] text-text_secondary">{label}</span>
      <div className="w-28 flex-shrink-0">{children}</div>
    </label>
  );
}

export default function GlobalAssumptionsSection({ config, readOnly, onSaved }) {
  const draft = useSectionDraft(config, "globalAssumptions", onSaved, "Global assumptions");
  const ga = draft.value;
  if (!ga) return null;

  const set = (key) => (v) => draft.update((d) => { d[key] = v; });

  return (
    <SectionShell
      title="Global Assumptions"
      subtitle="Rates and percentages applied to every estimate built on this config version."
      icon={SlidersHorizontal}
      draft={draft}
      readOnly={readOnly}
    >
      <div className="grid gap-6 lg:grid-cols-2">
        <SubSection title="Labor rates" hint="Burdened $/hour. See Labor Build-up for how the painting rate is derived.">
          {RATE_FIELDS.map((f) => (
            <LabeledCell key={f.key} label={f.label}>
              <CellNumber value={ga[f.key]} onChange={set(f.key)} disabled={readOnly} step="0.25" min={0} suffix={f.suffix} ariaLabel={f.label} />
            </LabeledCell>
          ))}
        </SubSection>

        <SubSection title="Markups & percentages" hint="Applied in the Bid Form cost waterfall.">
          {PCT_FIELDS.map((f) => (
            <LabeledCell key={f.key} label={f.label}>
              <CellPct value={ga[f.key]} onChange={set(f.key)} disabled={readOnly} min={0} ariaLabel={f.label} />
            </LabeledCell>
          ))}
        </SubSection>

        <SubSection title="Tool waste" hint="Extra material for loss / overspray, by field tool.">
          <div className="overflow-hidden rounded-[4px] border border-border">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className={TH}>Tool</th>
                  <th className={`${TH} w-32 text-right`}>Waste</th>
                </tr>
              </thead>
              <tbody>
                {TOOLS.map((t) => (
                  <tr key={t}>
                    <td className={TD}>{t}</td>
                    <td className={`${TD} py-1`}>
                      <CellPct
                        value={ga.toolWaste?.[t]}
                        onChange={(v) => draft.update((d) => { d.toolWaste[t] = v; })}
                        disabled={readOnly}
                        min={0}
                        ariaLabel={`${t} waste`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SubSection>

        <div className="flex flex-col gap-6">
          <SubSection title="Container sizes" hint="Gallons per container, used to round the Material List purchase quantities.">
            {CONTAINERS.map((c) => (
              <LabeledCell key={c.key} label={c.label}>
                <CellNumber
                  value={ga.containerSizes?.[c.key]}
                  onChange={(v) => draft.update((d) => { d.containerSizes[c.key] = v; })}
                  disabled={readOnly}
                  step="0.25"
                  min={0}
                  suffix="gal"
                  ariaLabel={`${c.label} size`}
                />
              </LabeledCell>
            ))}
          </SubSection>

          <SubSection title="Scheduling">
            {SCHEDULE_FIELDS.map((f) => (
              <LabeledCell key={f.key} label={f.label}>
                <CellNumber value={ga[f.key]} onChange={set(f.key)} disabled={readOnly} step="1" min={1} suffix={f.suffix} ariaLabel={f.label} />
              </LabeledCell>
            ))}
          </SubSection>
        </div>
      </div>
    </SectionShell>
  );
}
